/**
 * The answer key of a nurikabe is the wall. Kept apart from the form component so the eagerly
 * loaded module file never pulls the author's board into the player bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, SHADED, type NurikabeDefinition, type NurikabeSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately empty wall. `wallOf` pads it to a blank grid.
 */
export function emptySolution(_definition: NurikabeDefinition): NurikabeSolution {
  return { rows: [] };
}

/** Whether a stored value is a wall at all, whatever its size. */
export function isWall(value: unknown): value is NurikabeSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[#.]*$/.test(row));
}

/** Nothing queued: not a wall, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isWall(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a wall of the definition's size. */
export function wallOf(value: unknown, definition: NurikabeDefinition): string[] {
  if (!isWall(value)) return blankRows(definition.rows, definition.cols);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === SHADED ? SHADED : EMPTY)).join("")),
    definition.rows,
    definition.cols,
  );
}
